import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Avatar, Button, List, Typography } from 'antd';
import { DeleteOutlined, UserOutlined } from '@ant-design/icons';
import CountdownPill from '../../../Shared/CountdownPill';
import TypingIndicator from '../../../Shared/TypingIndicator';

const { Text } = Typography;

const ContactSidebarItem = ({
    contact,
    conversation,
    isActive,
    isMobile,
    isDarkMode,
    t,
    listThemeColors,
    bgColor,
    onSelect,
    hoveredContactId,
    setHoveredContactId,
    onDelete
}) => {
    const [secondsUntilProcessing, setSecondsUntilProcessing] = useState(() => {
        const raw = conversation?.processing_starts_in ?? 0;
        const next = parseInt(String(raw), 10);
        return Number.isFinite(next) && next > 0 ? next : 0;
    });

    const countdownEndsAtRef = useRef(0);
    const countdownVersionRef = useRef(0);
    const [countdownVersion, setCountdownVersion] = useState(0);

    const isWriting = Boolean(conversation?.is_writing);

    const previewText = useMemo(() => {
        const last_message = conversation?.last_message;
        const hasContent = Boolean(last_message?.content);
        const hasMedias = Array.isArray(last_message?.medias) && last_message.medias.length > 0;
        return !last_message
            ? t('no_messages_yet')
            : (!hasContent && !hasMedias && last_message.sender_type === 'agent')
                ? t('agent_declined_to_respond')
                : (!hasContent && hasMedias)
                    ? t('media_message')
                    : (last_message.content || t('no_messages_yet'));
    }, [conversation?.last_message, t]);

    // Sync countdown with conversation updates by anchoring to a real end timestamp.
    useEffect(() => {
        if (isWriting) {
            countdownEndsAtRef.current = 0;
            setSecondsUntilProcessing(0);
            return;
        }
        const raw = conversation?.processing_starts_in ?? 0;
        const next = parseInt(String(raw), 10);
        const s = Number.isFinite(next) && next > 0 ? next : 0;
        if (!s) {
            countdownEndsAtRef.current = 0;
            setSecondsUntilProcessing(0);
            return;
        }
        countdownEndsAtRef.current = Date.now() + s * 1000;
        setSecondsUntilProcessing(s);
        countdownVersionRef.current += 1;
        setCountdownVersion(countdownVersionRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conversation?.processing_starts_in, conversation?.id, conversation?.is_writing]);

    useEffect(() => {
        if (!countdownEndsAtRef.current) return;
        const id = setInterval(() => {
            const remainingMs = countdownEndsAtRef.current - Date.now();
            const next = remainingMs > 0 ? Math.ceil(remainingMs / 1000) : 0;
            setSecondsUntilProcessing((prev) => (prev === next ? prev : next));
            if (next <= 0) {
                countdownEndsAtRef.current = 0;
                clearInterval(id);
            }
        }, 250);
        return () => clearInterval(id);
    }, [countdownVersion]);

    const name = `${contact?.first_name || ''} ${contact?.last_name || ''}`.trim() || t('virtual_contact');

    return (
        <List.Item
            onClick={onSelect}
            onMouseEnter={() => setHoveredContactId(contact?.id)}
            onMouseLeave={() => setHoveredContactId(null)}
            style={{
                cursor: 'pointer',
                padding: isMobile ? '10px 14px' : '8px 16px',
                background: bgColor,
                transition: 'background-color 0.2s ease',
                position: 'relative'
            }}
        >
            <List.Item.Meta
                style={{ alignItems: 'center', margin: 0 }}
                avatar={
                    <Avatar
                        src={contact?.picture_url}
                        icon={!contact?.picture_url && <UserOutlined />}
                        size={isMobile ? 36 : 40}
                    />
                }
                title={<span style={{ color: listThemeColors.text, fontSize: isMobile ? 14 : undefined }}>{name}</span>}
                description={
                    <div style={{ display: 'flex', alignItems: 'center', minWidth: 0, marginTop: 2 }}>
                        {isWriting ? (
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <TypingIndicator />
                                <Text type="secondary" style={{ fontSize: 12, color: '#1890ff' }}>
                                    {t('agent_is_typing')}
                                </Text>
                            </div>
                        ) : (
                            <>
                                <Text
                                    ellipsis
                                    style={{
                                        color: listThemeColors.secondaryText,
                                        fontSize: 12,
                                        flex: '0 1 auto',
                                        minWidth: 0,
                                    }}
                                >
                                    {previewText}
                                </Text>
                                {secondsUntilProcessing > 0 && (
                                    <div style={{ display: 'inline-flex', alignItems: 'center', marginLeft: 6 }}>
                                        <CountdownPill
                                            seconds={secondsUntilProcessing}
                                            isDarkMode={isDarkMode}
                                            size="sm"
                                        />
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                }
            />
            {/* Hover actions */}
            <div
                style={{
                    position: 'absolute',
                    right: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    display: isMobile ? 'flex' : (hoveredContactId === contact?.id ? 'flex' : 'none'),
                    gap: 4
                }}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
            >
                <Button
                    type="text"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={onDelete}
                    title={t('delete')}
                />
            </div>
        </List.Item>
    );
};

export default ContactSidebarItem;













